import { TemplateGalleryModal } from '@/components/Modals/TemplateGalleryModal';
import UpgradeFreePlanModal from '@/components/Modals/UpgradeFreePlanModal';
import { useNavigateToAgentsEditor } from '@/hooks/navigation/useNavigateToAgentsEditor';
import { useGetAgents } from '@/pages/agents/api';
import { RemoveEmbedAgentModal } from '@/pages/agents/components/modals';
import { useGetAllVoices } from '@/pages/agents/hooks';
import { ActionBar, MyAgents, RecentVideos, Tutorials } from '@/pages/home/components';
import { DeleteLibraryItemModal } from '@/pages/home/components/DeleteLibraryItemModal';
import { MyAvatars } from '@/pages/home/components/MyAvatars';
import { useAuth } from '@/services/auth';
import { useTemplateGalleryModal } from '@/state/template-gallery/useTemplateGalleryModal';
import { useCurrentLibraryItems, useFilterVideos } from '@/state/video-library/videoLibrary';
import { AgentStatus, IAgent, PresenterUsecase, VideoType } from '@d-id/types';
import styled from '@emotion/styled';
import { useState } from 'react';
import { useAllAvatars, useAvatarsSorting } from '../avatar';
import ApikeyModal from './components/ApiKey/ApikeyModal';
import AvatarsDiscoverabilityBanner from './components/AvatarsDiscoverabilityBanner/AvatarsDiscoverabilityBanner';
import { MyTranslations } from './components/MyTranslations/MyTranslations';

const HomePageContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 32px;
`;

const Container = styled.div`
    display: flex;
    flex-direction: column;
    gap: 32px;
`;

const StyledAgents = styled(MyAgents)<{ isPrioritized: boolean }>`
    order: ${({ isPrioritized }) => (isPrioritized ? -1 : 0)};
`;

export const HomePage = () => {
    useGetAllVoices();
    const { isAnonymous, user } = useAuth();
    const { data: agentsData, isLoading: agentsLoading } = useGetAgents(isAnonymous);
    const { isVisuallyOpen } = useTemplateGalleryModal();
    const [removeEmbedModalAgent, setRemoveEmbedModalAgent] = useState<IAgent>();
    const { items: libraryItems } = useCurrentLibraryItems();
    const { getFilteredVideos } = useFilterVideos();

    const { allAvatars, userAvatars, isLoading: avatarsLoading } = useAllAvatars(PresenterUsecase.Video, 'all');
    const { sortAvatars } = useAvatarsSorting();
    const sortedAvatars = sortAvatars(allAvatars);

    const filteredLibraryItems = getFilteredVideos(libraryItems);
    const { navigateToAgentEditor } = useNavigateToAgentsEditor();
    const agents = [...(agentsData?.items || [])];
    const filteredAgents = agents
        .filter(agent => agent.status === AgentStatus.Done && agent.access !== 'public')
        .sort(agent => (agent.embed ? -1 : 1));

    const latestAgentDate = (filteredAgents && Math.max(...filteredAgents.map(agent => Date.parse(agent.created_at)))) || 0;
    const latestProjectDate = (filteredLibraryItems && Math.max(...filteredLibraryItems.map(item => Date.parse(item.created_at)))) || 0;

    const agentsFirst = latestAgentDate > latestProjectDate;

    const showAvatarBanner = !(
        avatarsLoading || userAvatars.some(({ type, created_by }) => type && type !== VideoType.TALK && created_by === user?.sub)
    );

    return (
        <HomePageContainer>
            <ActionBar />
            {<AvatarsDiscoverabilityBanner show={showAvatarBanner} />}

            <Container>
                <RecentVideos />
                <MyTranslations />
                <StyledAgents
                    onEditAgent={(agent: IAgent) => {
                        if (agent.embed) {
                            setRemoveEmbedModalAgent(agent);
                        } else {
                            navigateToAgentEditor({ agentId: agent.id, mixPanelFrom: 'my-agents' });
                        }
                    }}
                    isPrioritized={agentsFirst}
                    agents={filteredAgents}
                    isLoading={agentsLoading}
                />
                {<MyAvatars avatars={sortedAvatars} isLoading={avatarsLoading} />}
            </Container>

            <Container id="tutorials-container">
                <Tutorials />
            </Container>

            <DeleteLibraryItemModal />
            <ApikeyModal />
            <UpgradeFreePlanModal from="workspace" />
            {isVisuallyOpen && <TemplateGalleryModal from="Home" />}

            <RemoveEmbedAgentModal
                agent={removeEmbedModalAgent}
                isOpen={!!removeEmbedModalAgent}
                onClose={() => setRemoveEmbedModalAgent(undefined)}
            />
        </HomePageContainer>
    );
};
